import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

export default function Appointment() {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    preferred_time: '',
    notes: ''
  });

  const services = [
    'ENT Consultation'
  ];

  const timeSlots = [
    '10:00 AM',
    '10:30 AM',
    '11:00 AM',
    '11:30 AM',
    '12:00 PM',
    '12:30 PM',
    '01:00 PM',
    '01:30 PM'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        preferred_date: date ? format(date, 'yyyy-MM-dd') : '',
        language: i18n.language
      });
      toast.success(t('appointment.form.success'));
      setFormData({ name: '', phone: '', email: '', service: '', preferred_time: '', notes: '' });
      setDate(undefined);
    } catch (error) {
      toast.error(t('appointment.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-16 lg:py-24 bg-secondary/30">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6" data-testid="appointment-page-title">
            {t('appointment.title')}
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground" data-testid="appointment-page-subtitle">
            {t('appointment.subtitle')}
          </p>
          <div className="mt-4 inline-block bg-secondary px-6 py-3 rounded-lg">
            <p className="text-sm font-semibold text-primary">
              📅 Clinic Hours: Monday to Saturday | 10:00 AM - 2:00 PM
            </p>
          </div>
        </motion.div>

        <Card className="p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">{t('appointment.form.name')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="appointment-name-input"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t('appointment.form.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="appointment-phone-input"
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">{t('appointment.form.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="appointment-email-input"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="service">{t('appointment.form.service')}</Label>
              <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })} required>
                <SelectTrigger className="mt-2" data-testid="appointment-service-select">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service} value={service} data-testid={`service-option-${service.toLowerCase().replace(/\s+/g, '-')}`}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>{t('appointment.form.date')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal mt-2"
                      data-testid="appointment-date-picker"
                    >
                      <CalendarDays size={20} className="mr-2" />
                      {date ? format(date, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="time">{t('appointment.form.time')}</Label>
                <Select value={formData.preferred_time} onValueChange={(value) => setFormData({ ...formData, preferred_time: value })} required>
                  <SelectTrigger className="mt-2" data-testid="appointment-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time} data-testid={`time-option-${time.toLowerCase().replace(/\s+/g, '-')}`}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">{t('appointment.form.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                data-testid="appointment-notes-textarea"
                className="mt-2"
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full btn-primary"
              disabled={isSubmitting}
              data-testid="appointment-submit-button"
            >
              {isSubmitting ? t('appointment.form.submitting') : t('appointment.form.submit')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
